import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { DollarSign } from 'lucide-react';
import Layout from '../components/Layout';
import ExpensesTab from '../components/project/ExpensesTab';
import LaborSummaryTab from '../components/project/LaborSummaryTab';
import DetailInformationTab from '../components/project/DetailInformationTab';
import DetailPersonnelTab from '../components/project/DetailPersonnelTab';
import DetailNotesTab from '../components/project/DetailNotesTab';
import ProjectHistoryTab from '../components/project/ProjectHistoryTab';
import { projectsAPI, showErrorModal } from '../services/api';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { readResponse, readResponseJSON } from '../utils/fetchSafe';
import { getJobTypeCommission } from '../utils/jobTypeCommission';

const ProjectDetail = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const isAdmin = Boolean(user?.is_admin);
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);
  // Allow other pages (e.g., the Timeclock Back button) to deep-link to a tab
  // via ?tab=labor.
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'information');
  const [expenseOptions, setExpenseOptions] = useState([]);

  // Expenses state (kept in parent for saveProject)
  const [expenses, setExpenses] = useState([{ expense: '', date: '', memo: '', amount: '', percent: '100' }]);
  const [savedExpenses, setSavedExpenses] = useState([]);

  // Labor stats for header
  const [laborHours, setLaborHours] = useState(0);
  const [laborCost, setLaborCost] = useState(0);
  const [laborVehicle, setLaborVehicle] = useState(0);
  const [laborFuel, setLaborFuel] = useState(0);

  // Form fields
  const [status, setStatus] = useState('In Progress');
  const [customer, setCustomer] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [territory, setTerritory] = useState('');
  const [type, setType] = useState('');
  const [address, setAddress] = useState('');
  const [projectSource, setProjectSource] = useState('');
  const [prePayCash, setPrePayCash] = useState('0.00');
  const [preTaxValue, setPreTaxValue] = useState('');
  const [preTaxPlaceholder, setPreTaxPlaceholder] = useState(false);
  const [paidInFull, setPaidInFull] = useState(false);
  const [paymentType, setPaymentType] = useState('');
  const [claimNumber, setClaimNumber] = useState('');
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [invoiceSentDate, setInvoiceSentDate] = useState('');
  const [mgmtBonusPaid, setMgmtBonusPaid] = useState('');
  const [projectPaidDate, setProjectPaidDate] = useState('');
  const [pndDate, setPndDate] = useState('');
  const [estimateDate, setEstimateDate] = useState('');
  const [startDate, setStartDate] = useState('');
  const [completionDate, setCompletionDate] = useState('');
  const [restorationCorp, setRestorationCorp] = useState('');
  const [nonCommission, setNonCommission] = useState(false);
  const [authSigned, setAuthSigned] = useState(false);
  const [uploadRequired, setUploadRequired] = useState(false);
  const [notes, setNotes] = useState([]);
  const [newActivityCount, setNewActivityCount] = useState(0);
  const noteKeysRef = useRef(new Set());
  const hasNoteBaselineRef = useRef(false);
  const [showNoteForm, setShowNoteForm] = useState(false);
  const [newNoteText, setNewNoteText] = useState('');
  const [noteUrgent, setNoteUrgent] = useState(false);

  // Customer addresses for address dropdown
  const [customerAddresses, setCustomerAddresses] = useState([]);
  const [franchises, setFranchises] = useState([]);
  const [projectTypes, setProjectTypes] = useState([]);
  // Map of Job Type name -> commission % (drives the Expenses box / Profit Margin)
  const [typeCommissionMap, setTypeCommissionMap] = useState({});

  // Key Personnel state
  const [projectLeads, setProjectLeads] = useState([]);
  const [projectSources, setProjectSources] = useState([]);
  const [estimators, setEstimators] = useState([]);
  const [marketingReps, setMarketingReps] = useState([]);
  const [officeContacts, setOfficeContacts] = useState([]);
  const [productionEmployees, setProductionEmployees] = useState([]);
  const [selectedProjectLead, setSelectedProjectLead] = useState('');
  const [selectedEstimator, setSelectedEstimator] = useState('');
  const [estimatorAction, setEstimatorAction] = useState(false);
  const [selectedMarketingRep, setSelectedMarketingRep] = useState('');
  const [selectedOfficeContact, setSelectedOfficeContact] = useState('');
  const [officeAction, setOfficeAction] = useState(false);
  const [selectedProductionManager, setSelectedProductionManager] = useState('');
  const [pmCommissionPercent, setPmCommissionPercent] = useState('20');
  // Array of PM rows: [{ production_manager, pm_commission_percent }]. The first
  // row mirrors the legacy single-PM fields above for backwards compatibility
  // with the commission report. Add/remove rows from the Key Personnel tab.
  const [pmRows, setPmRows] = useState([{ production_manager: '', pm_commission_percent: '20' }]);

  const getNoteKey = (note) => [note?.created_at, note?.created_on, note?.created_by, note?.text]
    .map(value => String(value || '').trim()).join('|');

  const establishNoteBaseline = (noteList) => {
    noteKeysRef.current = new Set((noteList || []).map(getNoteKey));
    hasNoteBaselineRef.current = true;
  };

  useEffect(() => {
    noteKeysRef.current = new Set();
    hasNoteBaselineRef.current = false;
    setNewActivityCount(0);
    fetchProjectDetails();
    fetchPersonnelByRole();
    fetchProjectSources();
    fetchFranchises();
    fetchProjectTypes();
  }, [projectId]);

  useEffect(() => {
    const refreshNotes = async () => {
      try {
        const { notes: latestNotes = [] } = await projectsAPI.getNotes(projectId);
        if (!hasNoteBaselineRef.current) {
          establishNoteBaseline(latestNotes);
          setNotes(latestNotes);
          return;
        }
        const unseenCount = latestNotes.filter(note => !noteKeysRef.current.has(getNoteKey(note))).length;
        noteKeysRef.current = new Set(latestNotes.map(getNoteKey));
        setNotes(latestNotes);
        if (unseenCount && activeTab !== 'notes') setNewActivityCount(count => count + unseenCount);
      } catch {
        // Activity polling should never interrupt a user's project edits.
      }
    };
    const timer = window.setInterval(refreshNotes, 30 * 1000);
    return () => window.clearInterval(timer);
  }, [projectId, activeTab]);

  const fetchProjectTypes = async () => {
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(`${API_URL}/api/project-types`);
      const { data } = await readResponse(res);
      const map = {};
      const types = (data.project_types || []).map(t => t.name).filter(Boolean);
      (data.project_types || []).forEach(t => {
        const pct = parseFloat(t.commission);
        if (!isNaN(pct)) map[t.name] = pct;
      });
      setProjectTypes(types);
      setTypeCommissionMap(map);
    } catch { /* silent */ }
  };

  const fetchPersonnelByRole = async () => {
    const API_URL = process.env.REACT_APP_BACKEND_URL;
    const roles = [
      { role: 'Project Lead', setter: setProjectLeads },
      { role: 'Estimator', setter: setEstimators },
      { role: 'Marketing', setter: setMarketingReps },
      { role: 'Office', setter: setOfficeContacts },
      { role: 'Production', setter: setProductionEmployees },
    ];
    try {
      const results = await Promise.all(
        roles.map(r => fetch(`${API_URL}/api/employees?active=true&role=${encodeURIComponent(r.role)}`).then(readResponseJSON))
      );
      results.forEach((data, i) => roles[i].setter(data.employees || []));
    } catch { /* silent */ }
    try {
      const res = await fetch(`${API_URL}/api/project-expenses`);
      const { data } = await readResponse(res);
      setExpenseOptions(data.expenses || []);
    } catch { /* silent */ }
  };

  const fetchProjectSources = async () => {
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(`${API_URL}/api/project-sources`);
      const { data } = await readResponse(res);
      setProjectSources((data.sources || []).map(s => s.name));
    } catch { /* silent */ }
  };

  const fetchFranchises = async () => {
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(`${API_URL}/api/franchise-locations`);
      const { data } = await readResponse(res);
      setFranchises(data.franchises || []);
    } catch { /* silent */ }
  };

  const fetchProjectDetails = async () => {
    try {
      setLoading(true);
      const data = await projectsAPI.getOne(projectId);
      setProject(data);
      const reverseStatusMap = { ar: 'Accounts Receivable', closed: 'Closed', dead: 'Dead' };
      setStatus(data.job_status || reverseStatusMap[data.status] || 'In Progress');
      setCustomer(data.customer || '');
      setPhone(data.phone || '');
      setEmail(data.email || '');
      setTerritory(data.territory || '');
      setType(data.job_type || '');
      setAddress(data.address || '');
      setProjectSource(data.project_source || '');
      setPaymentType(data.job_payment || '');
      setClaimNumber(data.claim_number || '');
      setInvoiceNumber(data.invoice_number || '');
      setInvoiceSentDate(data.invoice_sent_date || '');
      setMgmtBonusPaid(data.mgmt_bonus_paid || '');
      setProjectPaidDate(data.project_paid_date || '');
      setRestorationCorp(data.restoration_opp || '');
      setNonCommission(data.non_commission_project || false);
      setAuthSigned(data.authorization_signed || false);
      setUploadRequired(data.upload_required || false);
      setPndDate(data.pnd_date || '');
      setEstimateDate(data.estimate_date || '');
      setStartDate(data.start_date || '');
      setCompletionDate(data.finish_date || '');
      setSelectedProjectLead(data.project_lead || '');
      setSelectedEstimator(data.estimator_contact || '');
      setEstimatorAction(data.estimator_action || false);
      setSelectedMarketingRep(data.marketing_rep || '');
      setSelectedOfficeContact(data.office_contact || '');
      setOfficeAction(data.office_action || false);
      setSelectedProductionManager(data.production_manager || '');
      setPmCommissionPercent(data.pm_commission_percent ?? '20');
      // Hydrate pm_rows. If pm_rows exists on the project, use it.
      // Otherwise seed from the legacy single-PM fields.
      if (Array.isArray(data.pm_rows) && data.pm_rows.length > 0) {
        setPmRows(data.pm_rows.map(r => ({
          production_manager: r.production_manager || '',
          pm_commission_percent: r.pm_commission_percent != null ? String(r.pm_commission_percent) : '20',
        })));
      } else {
        setPmRows([{
          production_manager: data.production_manager || '',
          pm_commission_percent: data.pm_commission_percent ?? '20',
        }]);
      }
      // Normalise the displayed pre-tax value so an integer like 5000 reads
      // as "5000.00" — matches the on-blur formatter and the legacy site.
      const rawPv = data.pre_tax_total_value;
      if (rawPv === null || rawPv === undefined || rawPv === '') {
        setPreTaxValue('');
      } else {
        const num = parseFloat(String(rawPv).replace(/[$,]/g, ''));
        setPreTaxValue(Number.isNaN(num) ? String(rawPv) : num.toFixed(2));
      }
      setPreTaxPlaceholder(data.pre_tax_placeholder || false);
      setPaidInFull(data.paid === 'Yes' || data.paid_in_full === true);
      let loadedNotes = [];
      if (data.notes_list && data.notes_list.length > 0) {
        loadedNotes = data.notes_list;
      } else if (data.notes) {
        // Legacy fallback: a single `notes` blob with no structured list.
        // Format is typically "Name (YYYY-MM-DD HH:MM:SS) - body of the note".
        // Parse out the author + timestamp so the columns aren't all dumped
        // into the Notes cell.
        const raw = String(data.notes);
        const m = raw.match(/^\s*([^()]+?)\s*\((\d{4}-\d{2}-\d{2}(?:[ T]\d{2}:\d{2}(?::\d{2})?)?)\)\s*[-–:]?\s*([\s\S]*)$/);
        if (m) {
          loadedNotes = [{ created_by: m[1].trim() || 'Unknown', created_on: m[2].trim(), text: m[3].trim() }];
        } else {
          // Couldn't parse — keep the original behaviour (Unknown / blank date / full text).
          loadedNotes = [{ created_by: 'Unknown', created_on: '', text: raw }];
        }
      }
      setNotes(loadedNotes);
      if (!hasNoteBaselineRef.current) establishNoteBaseline(loadedNotes);
      const loadedExpenses = data.expenses_list || [];
      setSavedExpenses(loadedExpenses);
      setExpenses([{ expense: '', date: '', memo: '', amount: '', percent: '100' }]);

      // Fetch customer addresses for address dropdown
      if (data.customer_id) {
        try {
          const API_URL = process.env.REACT_APP_BACKEND_URL;
          const custRes = await fetch(`${API_URL}/api/customers/${data.customer_id}`);
          if (custRes.ok) {
            const { data: custData } = await readResponse(custRes);
            setCustomerAddresses(custData.addresses || []);
          }
        } catch { /* silent */ }
      }

      // Fetch labor timeslips for header stats
      try {
        const API_URL = process.env.REACT_APP_BACKEND_URL;
        const [slipRes, empRes] = await Promise.all([
          fetch(`${API_URL}/api/timeslips?project_number=${encodeURIComponent(data.project_id || projectId)}`).then(readResponseJSON),
          fetch(`${API_URL}/api/employees`).then(readResponseJSON),
        ]);
        const slips = slipRes.timeslips || [];
        const rateMap = {};
        (empRes.employees || []).forEach(e => {
          const formal = `${e.last_name}, ${e.first_name}`;
          if (e.pay_rate) {
            rateMap[formal] = e.pay_rate;
            // Also index by raw `name` (OCR-imported timeslips use "X.Lastname").
            if (e.name) rateMap[e.name] = e.pay_rate;
          }
        });
        const totalHrs = slips.reduce((s, t) => s + (parseFloat(t.hours) || 0), 0);
        const totalCost = slips.reduce((s, t) => {
          // Prefer the slip's STORED amount (set by the OCR importer or by
          // manual edit) — it reflects the actual rate used on that specific
          // day, which may differ from the employee's current default
          // pay_rate. This must stay in lockstep with `slipAmount` in
          // LaborSummaryTab so the Expenses tile and Labor Summary footer
          // agree on the same dollar total.
          const stored = parseFloat(String(t.amount || '0').replace(/[$,]/g, '')) || 0;
          if (stored > 0) return s + stored;
          const h = parseFloat(t.hours) || 0;
          const r = parseFloat(rateMap[t.employee]) || 0;
          return s + (h * r);
        }, 0);
        const totalVehicle = slips.reduce(
          (s, t) => s + (parseFloat(String(t.vehicle || '0').replace(/[$,]/g, '')) || 0),
          0
        );
        const totalFuel = slips.reduce(
          (s, t) => s + (parseFloat(String(t.fuel_expense || '0').replace(/[$,]/g, '')) || 0),
          0
        );
        setLaborHours(totalHrs);
        setLaborCost(totalCost);
        setLaborVehicle(totalVehicle);
        setLaborFuel(totalFuel);
      } catch { /* silent */ }
    } catch (error) {
      toast.error('Failed to fetch project details');
    } finally {
      setLoading(false);
    }
  };

  const saveProject = async () => {
    // Re-entrancy guard: blocks rapid double-clicks on Save & Return /
    // Save & Refresh from firing two PUTs before the first resolves.
    if (savingRef.current) return false;
    savingRef.current = true;
    setSaving(true);
    const requiresCompletion = ['Completed', 'Accounts Receivable', 'Closed'].includes(status);
    if (requiresCompletion && !completionDate) {
      showErrorModal('Completion Date is required for projects in status ' + status);
      savingRef.current = false;
      setSaving(false);
      return false;
    }
    if (status === 'Accounts Receivable' && !invoiceSentDate) {
      // Accounts Receivable means an invoice has been issued. Without an
      // Invoice Sent Date the A/R board's aging metric (>60 / >90 days)
      // can't be computed, so block the status change until the user
      // supplies a date.
      showErrorModal('Invoice Sent Date is required to move a project to Accounts Receivable');
      savingRef.current = false;
      setSaving(false);
      return false;
    }
    if (status === 'Closed' && !projectPaidDate) {
      showErrorModal('Project Paid Date is required when closing out a job');
      savingRef.current = false;
      setSaving(false);
      return false;
    }
    // Save an in-progress note after the project fields. Notes use a dedicated
    // atomic endpoint so another open browser tab can never overwrite them.
    let pendingNote = null;
    if (newNoteText && newNoteText.trim()) {
      pendingNote = { text: newNoteText.trim(), urgent: noteUrgent };
    }
    const body = {
      job_status: status, customer, phone, email, job_type: type, address, job_payment: paymentType,
      territory, project_source: projectSource, claim_number: claimNumber,
      invoice_number: invoiceNumber, mgmt_bonus_paid: mgmtBonusPaid,
      invoice_sent_date: invoiceSentDate,
      project_paid_date: projectPaidDate,
      restoration_opp: restorationCorp,
      non_commission_project: nonCommission,
      authorization_signed: authSigned,
      upload_required: uploadRequired,
      pnd_date: pndDate, estimate_date: estimateDate,
      start_date: startDate, finish_date: completionDate,
      project_lead: selectedProjectLead, estimator_contact: selectedEstimator,
      estimator_action: estimatorAction,
      marketing_rep: selectedMarketingRep, office_contact: selectedOfficeContact,
      office_action: officeAction,
      production_manager: selectedProductionManager, pm_commission_percent: pmCommissionPercent,
      // Only persist PM rows that have an actual employee selected — match
      // the Expenses-tile logic and avoid storing 20%-default placeholders.
      pm_rows: pmRows.filter(r => r.production_manager),
      pre_tax_placeholder: preTaxPlaceholder,
      paid_in_full: paidInFull,
      expenses_list: [
        ...savedExpenses,
        ...expenses.filter(e => e.expense || e.memo || e.amount).map(e => {
          const now = new Date();
          const dateStr = `${now.getMonth()+1}/${now.getDate()}/${now.getFullYear()} ${now.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }).toLowerCase()}`;
          const updatedBy = (() => { try { const u = JSON.parse(localStorage.getItem('user')); return u?.name || ''; } catch { return ''; } })();
          return {
            ...e,
            amount: parseFloat(String(e.amount || '0').replace(/[$,]/g, '')) || 0,
            percent: parseFloat(String(e.percent || '100')) || 100,
            last_updated: dateStr,
            last_updated_by: updatedBy,
          };
        })
      ],
    };
    if (preTaxValue !== '') {
      body.pre_tax_total_value = parseFloat(preTaxValue) || 0;
    }
    try {
      const updated = await projectsAPI.update(projectId, body);
      // Admin cascade: when customer name/phone/address changed, the
      // backend pushes the change up to the customer record + sideways to
      // every sibling project. Toast the result so the admin sees it.
      const cascade = updated?.customer_cascade;
      if (cascade && (cascade.customer_updated || cascade.projects_updated)) {
        const bits = [];
        if (cascade.customer_updated) bits.push('customer record');
        if (cascade.projects_updated) bits.push(`${cascade.projects_updated} sibling project${cascade.projects_updated === 1 ? '' : 's'}`);
        toast.success(`Cascaded changes to ${bits.join(' and ')}.`, { duration: 5000 });
      }
      if (pendingNote) {
        try {
          const noteResult = await projectsAPI.addNote(projectId, pendingNote);
          noteKeysRef.current.add(getNoteKey(noteResult.note));
          setNotes(previousNotes => [noteResult.note, ...previousNotes]);
          setNewNoteText('');
          setNoteUrgent(false);
          setShowNoteForm(false);
        } catch {
          toast.error('Project changes saved, but the note was not saved. Please try again.');
          return false;
        }
      }
      return true;
    } finally {
      setSaving(false);
      savingRef.current = false;
    }
  };

  const handleSaveAndReturn = async () => {
    try {
      const saved = await saveProject();
      if (!saved) return;
      // Navigate to the board that matches the project's CURRENT job_status.
      // Using a fixed destination (instead of navigate(-1)) so the button works
      // even when the user arrived via a refresh / new tab / deep link — those
      // flows have no SPA history entry to step back to, and history.back()
      // would silently no-op. Bonus: if the user changed the project's status
      // during edit (e.g. Open → AR), this drops them on the correct new board.
      let dest = '/projects';
      if (status === 'Accounts Receivable') dest = '/projects/accounts-receivable';
      else if (status === 'Closed') dest = '/projects/closed';
      else if (status === 'Dead') dest = '/projects/dead';
      navigate(dest);
    } catch (e) {
      toast.error(e.message || 'Failed to save');
    }
  };

  const handleRefresh = async () => {
    try {
      const saved = await saveProject();
      if (saved) fetchProjectDetails();
    } catch (e) {
      toast.error(e.message || 'Failed to save');
    }
  };

  const handleTabChange = (tabKey) => {
    setActiveTab(tabKey);
    if (tabKey === 'notes') setNewActivityCount(0);
  };

  const handleAddNote = async () => {
    if (!newNoteText.trim()) return;
    try {
      const result = await projectsAPI.addNote(projectId, { text: newNoteText.trim(), urgent: noteUrgent });
      noteKeysRef.current.add(getNoteKey(result.note));
      setNotes(previousNotes => [result.note, ...previousNotes]);
      setNewNoteText('');
      setNoteUrgent(false);
      setShowNoteForm(false);
      toast.success('Note added');
    } catch {
      toast.error('Failed to save note. Your note is still available to retry.');
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="p-8 text-center">
          <div className="text-gray-500">Loading project details...</div>
        </div>
      </Layout>
    );
  }

  const baseExpenses = savedExpenses.reduce((s, e) => s + ((parseFloat(e.amount) || 0) * (parseFloat(e.percent) || 100) / 100), 0);
  const revenue = parseFloat(preTaxValue) || 0;
  // Headline numbers come straight from the backend `financials` block so
  // every surface (project detail, AR board, Insights, Commissions Earned)
  // agrees on the same math. Falls back to a simple sum while a project is
  // mid-edit and the server hasn't recomputed yet — close enough for the
  // brief window before Save & Refresh.
  const fin = project?.financials || null;
  const typeCommissionPct = getJobTypeCommission(typeCommissionMap, type) || 0;
  // While the local form is dirty we mirror the backend formula as best we
  // can from in-memory state so the cards react to edits in real time.
  const localNonMgmt = 0; // expense categories aren't loaded client-side yet
  const localEligible = (nonCommission || revenue <= 0) ? 0 : Math.max(0, (revenue - localNonMgmt) * typeCommissionPct / 100);
  const localMgmtTotal = baseExpenses + laborVehicle + laborFuel; // assume all tab expenses are mgmt until backend recomputes
  const localPmBucket = Math.max(0, localEligible - localMgmtTotal - laborCost);
  const localPmAssignedPct = pmRows.reduce((s, r) => s + (r.production_manager ? (parseFloat(r.pm_commission_percent || '0') || 0) : 0), 0);
  const localPmAssigned = localPmBucket * localPmAssignedPct / 100;
  const localActualCost = baseExpenses + laborCost + laborVehicle + laborFuel + localPmAssigned;
  const localExpenseBox = nonCommission
    ? baseExpenses + laborCost + laborVehicle + laborFuel
    : Math.max(localEligible, localActualCost);
  const projectExpenses = fin ? fin.expense_box : localExpenseBox;
  const profitMargin = fin ? fin.profit_margin : (revenue > 0 ? ((revenue - projectExpenses) / revenue * 100) : 0);
  // Hourly Labor Rate is profit-per-hour (matches legacy):
  //   (revenue − expense_box) / labor hours.
  const hourlyRate = fin ? fin.hourly_labor_rate : (laborHours > 0 ? ((revenue - projectExpenses) / laborHours) : 0);

  return (
    <Layout>
      <div className="bg-gray-100 min-h-screen">
        {/* Header */}
        <div className="bg-white shadow-sm p-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate(-1)} className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-4 py-2 rounded transition-colors text-sm" data-testid="back-btn">Back</button>
            <div>
              <div className="text-[#5B8DB8] text-lg mb-1" suppressHydrationWarning>
                Project # {project?.project_id || projectId}
                {phone && <span className="text-gray-500 text-sm ml-2">(Ph: {phone})</span>}
                {email && <span className="text-gray-500 text-sm ml-2">(Em: {email})</span>}
              </div>
              <h1 className="text-2xl text-[#5B8DB8]" suppressHydrationWarning>
                {customer} - {address}
              </h1>
            </div>
          </div>
          <div className="flex items-center gap-8">
            <div className="text-center border rounded px-4 py-2" data-testid="stat-subtotal">
              <div className="text-xs text-gray-500 mb-1">SUBTOTAL</div>
              <div className="text-xl">{revenue > 0 ? `$${revenue.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}` : '---'}</div>
            </div>
            <div className="text-center border rounded px-4 py-2" data-testid="stat-profit-margin">
              <div className="text-xs text-gray-500 mb-1">PROFIT MARGIN</div>
              <div className="text-xl">{revenue > 0 ? `${profitMargin.toFixed(2)}%` : '---'}</div>
            </div>
            <div className="text-center border rounded px-4 py-2" data-testid="stat-hourly-rate">
              <div className="text-xs text-gray-500 mb-1">HOURLY LABOR RATE</div>
              <div className="text-xl">{laborHours > 0 ? `$${hourlyRate.toFixed(2)}` : '---'}</div>
            </div>
            <div className="text-center border rounded px-4 py-2" data-testid="stat-labor-hrs">
              <div className="text-xs text-gray-500 mb-1">LABOR HRS</div>
              <div className="text-2xl font-light">{laborHours.toFixed(2)} <span className="text-sm">HRS</span></div>
            </div>
            <div className="text-center border rounded px-4 py-2" data-testid="stat-expenses">
              <div className="text-xs text-gray-500 mb-1">EXPENSES</div>
              <div className="text-2xl font-light">${projectExpenses.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex">
          {/* Left Sidebar Navigation */}
          <div className="w-64 bg-white shadow-sm">
            <div className="p-4 flex gap-2">
              <button onClick={handleSaveAndReturn} disabled={saving} className="bg-[#4ECFDB] hover:bg-[#3AB8C5] text-white px-4 py-2 rounded transition-colors text-sm disabled:opacity-60 disabled:cursor-not-allowed" data-testid="save-return-btn">{saving ? 'Saving…' : 'Save & Return'}</button>
              <button onClick={handleRefresh} disabled={saving} className="border border-gray-300 hover:bg-gray-50 text-gray-600 px-4 py-2 rounded transition-colors text-sm disabled:opacity-60 disabled:cursor-not-allowed" data-testid="save-refresh-btn">{saving ? 'Saving…' : 'Save & Refresh'}</button>
            </div>
            <nav className="py-2">
              {[
                { key: 'information', icon: '\u26A1', label: 'Project Information' },
                { key: 'personnel', icon: '\uD83D\uDC64', label: 'Key Personnel' },
                { key: 'expenses', icon: null, label: 'Expenses', lucideIcon: DollarSign, iconClass: 'text-green-500' },
                { key: 'labor', icon: '\uD83D\uDCCA', label: 'Labor Summary' },
                { key: 'notes', icon: '\uD83D\uDCDD', label: 'Notes' },
                ...(isAdmin ? [{ key: 'history', icon: '\uD83D\uDD52', label: 'History' }] : []),
              ].map(tab => (
                <button
                  key={tab.key}
                  onClick={() => handleTabChange(tab.key)}
                  className={`w-full text-left px-6 py-3 flex items-center gap-3 border-l-4 border-b border-gray-200 ${
                    activeTab === tab.key
                      ? 'bg-gray-100 text-red-500 border-l-red-500'
                      : 'text-gray-600 hover:bg-gray-50 border-l-transparent'
                  }`}
                  data-testid={`project-detail-tab-${tab.key}`}
                >
                  {tab.lucideIcon ? (
                    <tab.lucideIcon className={`w-5 h-5 shrink-0 ${tab.iconClass || ''}`} />
                  ) : (
                    <span className={`min-w-[20px] w-5 text-center inline-flex items-center justify-center shrink-0 ${tab.iconClass || ''}`}>{tab.icon}</span>
                  )}
                  <span>{tab.label}</span>
                  {tab.key === 'notes' && newActivityCount > 0 && (
                    <span className="ml-auto min-w-5 h-5 px-1 inline-flex items-center justify-center rounded-full bg-[#D94F4F] text-white text-xs font-semibold" data-testid="notes-new-activity-count" aria-label={`${newActivityCount} new notes`}>
                      {newActivityCount > 99 ? '99+' : newActivityCount}
                    </span>
                  )}
                </button>
              ))}
            </nav>
          </div>

          {/* Main Content Area */}
          <div className="flex-1 p-8">
            {activeTab === 'information' && (
              <DetailInformationTab
                status={status} setStatus={setStatus}
                customer={customer} setCustomer={setCustomer}
                phone={phone} setPhone={setPhone}
                email={email} setEmail={setEmail}
                territory={territory} setTerritory={setTerritory}
                type={type} setType={setType}
                address={address} setAddress={setAddress}
                customerAddresses={customerAddresses}
                franchises={franchises}
                projectTypes={projectTypes}
                projectSource={projectSource} setProjectSource={setProjectSource}
                projectSources={projectSources}
                pndDate={pndDate} setPndDate={setPndDate}
                estimateDate={estimateDate} setEstimateDate={setEstimateDate}
                startDate={startDate} setStartDate={setStartDate}
                completionDate={completionDate} setCompletionDate={setCompletionDate}
                preTaxValue={preTaxValue} setPreTaxValue={setPreTaxValue}
                preTaxPlaceholder={preTaxPlaceholder} setPreTaxPlaceholder={setPreTaxPlaceholder}
                paymentType={paymentType} setPaymentType={setPaymentType}
                claimNumber={claimNumber} setClaimNumber={setClaimNumber}
                invoiceNumber={invoiceNumber} setInvoiceNumber={setInvoiceNumber}
                invoiceSentDate={invoiceSentDate} setInvoiceSentDate={setInvoiceSentDate}
                paidInFull={paidInFull} setPaidInFull={setPaidInFull}
                mgmtBonusPaid={mgmtBonusPaid} setMgmtBonusPaid={setMgmtBonusPaid}
                projectPaidDate={projectPaidDate} setProjectPaidDate={setProjectPaidDate}
                restorationCorp={restorationCorp} setRestorationCorp={setRestorationCorp}
                nonCommission={nonCommission} setNonCommission={setNonCommission}
                authSigned={authSigned} setAuthSigned={setAuthSigned}
                uploadRequired={uploadRequired} setUploadRequired={setUploadRequired}
                typeCommissionMap={typeCommissionMap}
              />
            )}

            {activeTab === 'personnel' && (
              <DetailPersonnelTab
                projectId={projectId}
                selectedProjectLead={selectedProjectLead} setSelectedProjectLead={setSelectedProjectLead} projectLeads={projectLeads}
                selectedEstimator={selectedEstimator} setSelectedEstimator={setSelectedEstimator} estimators={estimators}
                estimatorAction={estimatorAction} setEstimatorAction={setEstimatorAction}
                selectedMarketingRep={selectedMarketingRep} setSelectedMarketingRep={setSelectedMarketingRep} marketingReps={marketingReps}
                selectedOfficeContact={selectedOfficeContact} setSelectedOfficeContact={setSelectedOfficeContact} officeContacts={officeContacts}
                officeAction={officeAction} setOfficeAction={setOfficeAction}
                selectedProductionManager={selectedProductionManager} setSelectedProductionManager={setSelectedProductionManager} productionEmployees={productionEmployees}
                pmCommissionPercent={pmCommissionPercent} setPmCommissionPercent={setPmCommissionPercent}
                pmRows={pmRows} setPmRows={setPmRows}
              />
            )}

            {activeTab === 'expenses' && (
              <ExpensesTab
                expenses={expenses}
                setExpenses={setExpenses}
                savedExpenses={savedExpenses}
                setSavedExpenses={setSavedExpenses}
                expenseOptions={expenseOptions}
              />
            )}

            {activeTab === 'labor' && (
              <LaborSummaryTab projectId={project?.project_id || projectId} />
            )}

            {activeTab === 'notes' && (
              <DetailNotesTab
                notes={notes}
                uploadRequired={uploadRequired}
                showNoteForm={showNoteForm} setShowNoteForm={setShowNoteForm}
                newNoteText={newNoteText} setNewNoteText={setNewNoteText}
                noteUrgent={noteUrgent} setNoteUrgent={setNoteUrgent}
                handleAddNote={handleAddNote}
              />
            )}

            {activeTab === 'history' && isAdmin && (
              <ProjectHistoryTab projectId={project?.project_id || projectId} />
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default ProjectDetail;
